/**
 * Smarty US Autocomplete Pro API Service
 *
 * Provides address autocomplete directly from the frontend using Smarty's
 * embedded key authentication. Returns USPS-verified addresses with
 * 2-letter state codes.
 *
 * @see https://www.smarty.com/docs/cloud/us-autocomplete-pro-api
 */

import * as Sentry from '@sentry/react';

const SMARTY_API_URL = 'https://us-autocomplete-pro.api.smarty.com/lookup';

/**
 * Address suggestion returned by Smarty API
 */
export interface SmartySuggestion {
  /** Street address (e.g., "123 Main St") */
  street_line: string;
  /** Secondary address info (e.g., "Apt 1", "Suite 200") */
  secondary: string;
  /** City name */
  city: string;
  /** 2-letter state code (e.g., "NY", "CA") */
  state: string;
  /** 5-digit ZIP code */
  zipcode: string;
  /** Number of secondary addresses at this location (for apartments/units) */
  entries?: number;
}

interface SmartyApiResponse {
  suggestions: SmartySuggestion[];
}

/**
 * Fetch address suggestions from Smarty US Autocomplete Pro API
 *
 * @param search - Partial address string (minimum 3 characters recommended)
 * @param options - Optional filtering parameters
 * @returns Array of address suggestions with 2-letter state codes
 *
 * @example
 * const suggestions = await fetchAddressSuggestions('123 main st');
 * // Returns: [{ street_line: '123 Main St', city: 'New York', state: 'NY', ... }]
 */
export async function fetchAddressSuggestions(
  search: string,
  options?: {
    /** Limit results to specific states (2-letter codes separated by semicolons, e.g., "NY;CA") */
    includeOnlyStates?: string;
    /** Maximum number of results (1-10, default 10) */
    maxResults?: number;
  },
): Promise<SmartySuggestion[]> {
  const key = import.meta.env.VITE_SMARTY_EMBEDDED_KEY;

  if (!key) {
    Sentry.captureMessage(
      'Smarty embedded key not configured. Address autocomplete disabled.',
      {
        level: 'warning',
        tags: { service: 'smarty' },
      },
    );
    return [];
  }

  if (!search || search.length < 3) {
    return [];
  }

  const params = new URLSearchParams({
    key,
    search: search.trim(),
    source: 'postal', // Only return USPS-verified addresses
  });

  if (options?.includeOnlyStates) {
    params.set('include_only_states', options.includeOnlyStates);
  }

  if (options?.maxResults) {
    params.set('max_results', String(options.maxResults));
  }

  try {
    const response = await fetch(`${SMARTY_API_URL}?${params.toString()}`);

    if (!response.ok) {
      if (response.status === 401) {
        Sentry.captureException(
          new Error(
            'Smarty API: Invalid embedded key or hostname not authorized',
          ),
          {
            tags: { service: 'smarty', status: response.status },
          },
        );
      } else if (response.status === 429) {
        Sentry.captureMessage(
          'Smarty API: Rate limited. Consider adding IP to authorized hosts.',
          {
            level: 'warning',
            tags: { service: 'smarty', status: response.status },
          },
        );
      }
      return [];
    }

    const data: SmartyApiResponse = await response.json();
    return data.suggestions || [];
  } catch (error) {
    Sentry.captureException(error, {
      tags: { service: 'smarty' },
    });
    return [];
  }
}

/**
 * Convert Smarty suggestion to a standardized address format
 */
export function toStandardAddress(suggestion: SmartySuggestion) {
  return {
    address_line1: suggestion.street_line,
    address_line2: suggestion.secondary || null,
    city: suggestion.city,
    state: suggestion.state, // Already 2-letter code
    postal_code: suggestion.zipcode,
    country: 'US',
  };
}
